import React, { useContext, useEffect, useState } from "react";
import { Col, Row, FormGroup, Label, Input } from "reactstrap";
import InputSelectCustom from "../../../components/Common/InputSelectCustom";
import BizinBackend from "../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../Hooks/useGetCsrfToken";
import { useSelector } from "react-redux";
import { DF_OT0022Context } from "../context";

export default function GridHeader() {
  const { isLoading, setParamsOnFilter, setTriggerOnFilter, userProfile } =
    useContext(DF_OT0022Context);

  const csrfToken = useGetCsrfToken();
  const user = useSelector((state) => state.login.user);

  const [empresasOrigem, setEmpresasOrigem] = useState([]);
  const [empresasDestino, setEmpresasDestino] = useState([]);
  const [locaisEntrega, setLocaisEntrega] = useState([]);

  const [origemSelected, setOrigemSelected] = useState({ value: "", label: "Todas" });
  const [destinoSelected, setDestinoSelected] = useState({ value: "", label: "Todas" });
  const [localSelected, setLocalSelected] = useState({ value: "", label: "Todos" });
  const [statusSelected, setStatusSelected] = useState("");

  useEffect(() => {
    let isMounted = true;

    const carregarEmpresas = async () => {
      try {
        const idUsuario = parseInt(userProfile?.ID_Usuario || 0, 10);

        const paramsOrigem = { 1: idUsuario, 2: "pt-BR", 3: "ORIGEM", 4: "NF" };
        const paramsDestino = { 1: idUsuario, 2: "pt-BR", 3: "DESTINO", 4: "NF" };

        const [resOrigem, resDestino] = await Promise.all([
          BizinBackend.executeProcedure(
            paramsOrigem,
            "FRONT_Componente_Combo_Empresas",
            csrfToken,
            user?.access_token
          ),
          BizinBackend.executeProcedure(
            paramsDestino,
            "FRONT_Componente_Combo_Empresas",
            csrfToken,
            user?.access_token
          ),
        ]);

        if (!isMounted) return;

        if (Array.isArray(resOrigem)) {
          const dadosOrigem = resOrigem.map((item) => ({
            value: item.ID_Empresa.toString(),
            label: item.Nome_Empresa,
          }));
          setEmpresasOrigem([{ value: "", label: "Todas" }, ...dadosOrigem]);
        }

        if (Array.isArray(resDestino)) {
          const dadosDestino = resDestino.map((item) => ({
            value: item.ID_Empresa.toString(),
            label: item.Nome_Empresa,
          }));
          setEmpresasDestino([{ value: "", label: "Todas" }, ...dadosDestino]);
        }
      } catch (e) {
        console.error("Erro ao carregar combos de empresas:", e);
      }
    };

    if (userProfile?.ID_Usuario) {
      carregarEmpresas();
    }

    return () => {
      isMounted = false;
    };
  }, [userProfile?.ID_Usuario, csrfToken, user?.access_token]);

  useEffect(() => {
    let isMounted = true;

    if (!destinoSelected.value) {
      setLocaisEntrega([{ value: "", label: "Todos" }]);
      return;
    }

    const carregarLocais = async () => {
      try {
        const paramsLocais = {
          1: parseInt(destinoSelected.value, 10),
          2: null,
        };

        const resLocais = await BizinBackend.executeProcedure(
          paramsLocais,
          "SRM_Agendamento_Get_Lista_Locais_Entrega",
          csrfToken,
          user?.access_token
        );

        if (!isMounted) return;

        if (Array.isArray(resLocais)) {
          const dados = resLocais.map((item) => ({
            value: item.ID_Local_Entrega.toString(),
            label: item.Descricao_Local_Entrega,
          }));
          setLocaisEntrega([{ value: "", label: "Todos" }, ...dados]);
        } else {
          setLocaisEntrega([{ value: "", label: "Todos" }]);
        }
      } catch (e) {
        console.error("Erro ao carregar locais de entrega:", e);
      }
    };

    carregarLocais();

    return () => {
      isMounted = false;
    };
  }, [destinoSelected.value, csrfToken, user?.access_token]);

  const onSubmitFilter = () => {
    const objetoFiltroPlano = {
      Filtro_ID_Empresa_Origem: origemSelected?.value || "",
      Filtro_ID_Empresa_Destino: destinoSelected?.value || "",
      Filtro_ID_Local_Entrega: localSelected?.value || "",
      Filtro_Status: statusSelected || "",
    };

    setParamsOnFilter(objetoFiltroPlano);
    setTriggerOnFilter(true);
  };

  return (
    <Row className="mb-2">
      <Col lg={12} className={isLoading ? "disabled" : ""}>
        <h4 className="mb-3">Gestão de limite de agendamento</h4>

        <div className="bg-white mb-3 card-content shadow-card p-3">
          <Row className="align-items-end gx-3">
            {/* Empresa Origem */}
            <Col lg={2} md={6}>
              <FormGroup className="mb-0">
                <Label className="text-color-cinza4 font-size-12 mb-1">
                  Empresa Origem
                </Label>
                <InputSelectCustom
                  id="Filtro_ID_Empresa_Origem"
                  name="Empresa Origem"
                  stateSelected={origemSelected}
                  optionsData={empresasOrigem}
                  placeholder="Todas"
                  loadOptions={(val, cb) =>
                    cb(
                      empresasOrigem.filter((o) =>
                        o.label.toLowerCase().includes(val.toLowerCase())
                      )
                    )
                  }
                  handleOnChange={(selected) =>
                    setOrigemSelected(selected || { value: "", label: "Todas" })
                  }
                />
              </FormGroup>
            </Col>

            <Col lg={2} md={6}>
              <FormGroup className="mb-0">
                <Label className="text-color-cinza4 font-size-12 mb-1">
                  Empresa Destino
                </Label>
                <InputSelectCustom
                  id="Filtro_ID_Empresa_Destino"
                  name="Empresa Destino"
                  stateSelected={destinoSelected}
                  optionsData={empresasDestino}
                  placeholder="Todas"
                  loadOptions={(val, cb) =>
                    cb(
                      empresasDestino.filter((o) =>
                        o.label.toLowerCase().includes(val.toLowerCase())
                      )
                    )
                  }
                  handleOnChange={(selected) => {
                    setDestinoSelected(selected || { value: "", label: "Todas" });
                    setLocalSelected({ value: "", label: "Todos" });
                  }}
                />
              </FormGroup>
            </Col>

            <Col lg={2} md={6}>
              <FormGroup className="mb-0">
                <Label className="text-color-cinza4 font-size-12 mb-1">
                  Local de Entrega
                </Label>
                <InputSelectCustom
                  id="Filtro_ID_Local_Entrega"
                  name="Local de Entrega"
                  stateSelected={localSelected}
                  optionsData={locaisEntrega}
                  placeholder="Todos"
                  loadOptions={(val, cb) =>
                    cb(
                      locaisEntrega.filter((o) =>
                        o.label.toLowerCase().includes(val.toLowerCase())
                      )
                    )
                  }
                  handleOnChange={(selected) =>
                    setLocalSelected(selected || { value: "", label: "Todos" })
                  }
                />
              </FormGroup>
            </Col>

            <Col lg={2} md={6}>
              <FormGroup className="mb-0">
                <Label className="text-color-cinza4 font-size-12 mb-1">
                  Status
                </Label>
                <Input
                  type="select"
                  name="Filtro_Status"
                  id="Filtro_Status"
                  className="form-select text-color-cinza5"
                  value={statusSelected}
                  onChange={(e) => setStatusSelected(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Desativado">Desativado</option>
                </Input>
              </FormGroup>
            </Col>

            <Col lg={4} md={12} className="d-flex justify-content-end mt-3 mt-lg-0">
              <button
                type="button"
                className="btn btn-primary-dark text-white fw-semibold"
                onClick={onSubmitFilter}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin me-1" /> Pesquisando...
                  </>
                ) : (
                  "Pesquisar"
                )}
              </button>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
}